import { FC, memo, useEffect, useMemo, useRef } from 'react';
import { Label } from '../atoms/Label';
import { useAtomValue } from 'jotai';
import { toast, toastAtom } from '../../store/toast';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircle, CheckCircle2, TriangleAlert } from 'lucide-react';

interface ToastMessageProps {
  message: string;
  icon?: React.ReactNode;
  toastType: EToastType;
  className?: React.HTMLAttributes<HTMLDivElement>;
  isOpen: boolean;
  index: number;
  duration?: number;
}

export const TOAST_DURATIONS = {
  DEFAULT: 1500,
  ERROR: 1500,
  WARNING: 1500,
  VALIDATION: 2100,
} as const;

export enum EToastType {
  alert,
  success,
  warning,
}

export interface IToast {
  type: EToastType;
  message: string;
  isOpen: boolean;
  icon?: React.ReactNode;
  id?: string;
  options?: {
    duration?: number;
    onClick?: () => void;
    onClose?: () => void;
  };
}

export const ToastContainer: FC = memo(() => {
  const toastQueue = useAtomValue(toastAtom);
  const timeoutRef = useRef<any[]>([]);

  const mappedToast = useMemo(() => {
    if (!toastQueue.length) return [];

    return toastQueue.map((obj) => ({
      ...obj,
      options: {
        ...obj.options,
        onClose: () => {
          toast.shift();
          obj.options?.onClose?.();
        },
      },
    }));
  }, [toastQueue]);

  useEffect(() => {
    if (toastQueue.length) {
      const latestToast = toastQueue?.[toastQueue.length - 1];
      const currentTimeout = setTimeout(() => {
        if (toastQueue.length) {
          toast.shift();
          clearTimeout(currentTimeout);
          timeoutRef.current?.shift();
        }
      }, latestToast?.options?.duration ?? TOAST_DURATIONS.VALIDATION); // timeout on close toast

      timeoutRef.current?.push(currentTimeout);

      return () => {
        if (timeoutRef.current) {
          clearTimeout(currentTimeout);
          timeoutRef.current?.shift();
        }
      };
    } else if (timeoutRef.current?.length) {
      clearTimeout(timeoutRef.current?.[0]);
      timeoutRef.current?.shift();
    }
  }, [toastQueue]);

  // R9 zombie-toast fix: when the queue is empty, unmount the whole toast subtree. The exit
  // animation previously kept the removed toast's DOM alive via AnimatePresence, and under
  // main-thread pressure (R7 freezes) the exit never completed - orphaning a stale validation
  // message that stayed rendered across screens and misled the next entry. A guaranteed
  // unmount beats a 0.3s exit slide.
  if (!mappedToast.length) return null;

  return (
    // pointer-events-none: this full-width fixed strip sits OVER the header - without it,
    // every click on BACK/menu was swallowed while a toast was visible (proven live via
    // elementFromPoint), which read as "buttons need two clicks". The cards themselves
    // restore pointer-events so any future interactive toast still works.
    <div className="pointer-events-none fixed top-4 z-50 flex w-full flex-col items-center space-y-4 overflow-hidden bg-transparent pb-4">
      <AnimatePresence>
        {mappedToast.map((t, index) => (
          <ToastMessage
            key={t.id}
            index={index}
            toastType={t.type}
            icon={t.icon}
            {...t}
          />
        ))}
      </AnimatePresence>
    </div>
  );
});

const ToastMessage = ({
  message,
  toastType,
  icon,
  index,
  isOpen,
}: ToastMessageProps) => {
  const toastBackgroundColor = () => {
    switch (toastType) {
      case EToastType.alert:
        return 'bg-linear-to-r from-red-500 to-red-700 shadow-lg';
      case EToastType.warning:
        return 'bg-linear-to-r from-amber-400 to-amber-600 shadow-lg';
      case EToastType.success:
        return 'bg-linear-to-r from-green-500 to-green-700 shadow-lg';
    }
  };

  const toastIcon = () => {
    switch (toastType) {
      case EToastType.alert:
        return <AlertCircle className="size-6" color="white" />;
      case EToastType.warning:
        return <TriangleAlert className="size-6" color="white" />;
      case EToastType.success:
        return <CheckCircle2 className="size-6" color="white" />;
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <motion.div
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -80, opacity: 0 }}
        transition={{ duration: 0.3, delay: 0.1 * index }}
        className={`mb-2 flex w-11/12 justify-center p-4 ${toastBackgroundColor()} pointer-events-auto rounded-lg shadow-sm`}
      >
        <div className="flex w-full items-center justify-center">
          {icon && (
            <div className="mr-4 flex size-10 animate-bounce items-center justify-center rounded-full bg-white/30">
              {icon}
            </div>
          )}
          {message && (
            <div className="mt-1 flex items-center justify-center space-x-4 text-center">
              {toastIcon()}
              <Label
                titleType="body"
                className="w-full items-center justify-center! text-xl text-white"
              >
                {message}
              </Label>
            </div>
          )}
        </div>
      </motion.div>
    </>
  );
};
